/**
 * exchangeRateConstants.ts
 * Shared constants + small formatters for the Admin → Exchange Rates page.
 */

/** Base currency — rendered as a read-only 1.0000 row, never editable/removable. */
export const BASE_CURRENCY = 'CAD';

/**
 * Default currencies the backend always returns from GET /current. They cannot
 * be removed from the table (removing one just clears its rate back to null).
 * Display order on the page is CAD (base) then USD, EUR, GBP, AUD; the API
 * itself returns the non-CAD set sorted ascending.
 */
export const DEFAULT_CURRENCIES = ['AUD', 'EUR', 'GBP', 'USD'] as const;

/** Preferred display order for the default currencies (most-used first). */
export const PREFERRED_ORDER = ['USD', 'EUR', 'GBP', 'AUD'] as const;

/** True when a currency is part of the non-removable default set. */
export function isDefaultCurrency(currency: string): boolean {
  return (DEFAULT_CURRENCIES as readonly string[]).includes(currency.toUpperCase());
}

/** Validate a candidate 3-letter ISO 4217 code; returns the uppercased code or null. */
export function normalizeCurrencyCode(raw: string): string | null {
  const code = raw.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
}

/** Format an ISO-8601 timestamp into a short local date+time; em-dash when null. */
export function formatUpdatedAt(iso: string | null): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Sort the current-rates list for display: preferred defaults first (USD, EUR,
 * GBP, AUD), then any other currencies alphabetically.
 */
export function sortForDisplay<T extends { currency: string }>(rows: T[]): T[] {
  const rank = (c: string): number => {
    const idx = (PREFERRED_ORDER as readonly string[]).indexOf(c.toUpperCase());
    return idx === -1 ? PREFERRED_ORDER.length : idx;
  };
  return [...rows].sort((a, b) => {
    const ra = rank(a.currency);
    const rb = rank(b.currency);
    if (ra !== rb) return ra - rb;
    return a.currency.localeCompare(b.currency);
  });
}
